import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as XLSX from "xlsx";
import { Stack, HStack } from "@astryxdesign/core/Stack";
import { Grid } from "@astryxdesign/core/Grid";
import { Text, Heading } from "@astryxdesign/core/Text";
import { Card } from "@astryxdesign/core/Card";
import { Badge } from "@astryxdesign/core/Badge";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Selector } from "@astryxdesign/core/Selector";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Tooltip } from "@astryxdesign/core/Tooltip";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import {
  Table,
  pixel,
  proportional,
  useTableSortable,
  useTableStickyColumns,
  useTablePagination,
  paginateData,
} from "@astryxdesign/core/Table";
import { reportsAPI, handleAPIError } from "../../services/api";
import AstryxTheme from "../../theme/AstryxTheme";
import "../../fontawesome";

const GRUPO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "assescont", label: "Assescont" },
  { value: "comsind", label: "Comsind" },
  { value: "advisa", label: "Advisa" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function competenciaMmAaaaToApi(value) {
  const s = (value || "").trim().replace(/\s/g, "");
  const match = s.match(/^(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = parseInt(match[1], 10);
  const year = parseInt(match[2], 10);
  if (month < 1 || month > 12 || year < 1900 || year > 2100) return null;
  return `${year}-${String(month).padStart(2, "0")}`;
}

function apiCompetenciaToMmAaaa(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return "";
  return `${match[2]}/${match[1]}`;
}

function currentCompetenciaMmAaaa() {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
}

/** Mini barra de duas cores (verde/laranja), estilo "Hub x Portal" e "Importado". */
function TwoColorBar({ greenValue, orangeValue, total, greenLabel, orangeLabel }) {
  const safeTotal = total > 0 ? total : 1;
  const greenPct = Math.min(100, Math.max(0, (greenValue / safeTotal) * 100));
  const orangePct = Math.min(100 - greenPct, Math.max(0, (orangeValue / safeTotal) * 100));

  return (
    <Stack gap={1} width="100%" style={{ maxWidth: 90 }}>
      <HStack gap={0} width="100%" style={{ height: 5 }}>
        <div
          style={{
            width: `${greenPct}%`,
            backgroundColor: "var(--conf-lanc-green, #22c55e)",
            borderRadius: orangePct > 0 ? "3px 0 0 3px" : "3px",
            height: "100%",
          }}
        />
        {orangePct > 0 && (
          <div
            style={{
              width: `${orangePct}%`,
              backgroundColor: "var(--conf-lanc-orange, #f97316)",
              borderRadius: "0 3px 3px 0",
              height: "100%",
            }}
          />
        )}
        {greenPct === 0 && orangePct === 0 && (
          <div
            style={{
              width: "100%",
              backgroundColor: "var(--conf-lanc-empty, #e5e7eb)",
              borderRadius: 3,
              height: "100%",
            }}
          />
        )}
      </HStack>
      <HStack gap={2} hAlign="between">
        <Text size="xs" type="supporting">
          {greenLabel}
        </Text>
        {orangeValue > 0 && (
          <Text size="xs" type="supporting">
            {orangeLabel}
          </Text>
        )}
      </HStack>
    </Stack>
  );
}

export default function ConferenciaLancamentos() {
  const [competenciaInput, setCompetenciaInput] = useState(currentCompetenciaMmAaaa());
  const [grupo, setGrupo] = useState("");
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [competenciaResolved, setCompetenciaResolved] = useState("");
  const [importDataAvailable, setImportDataAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "importado", direction: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const load = async (mmAaaa, grupoValue) => {
    const competenciaApi = competenciaMmAaaaToApi(mmAaaa);
    if (mmAaaa && !competenciaApi) {
      setError("Competência inválida. Use o formato MM/AAAA.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await reportsAPI.conferenciaLancamentos(
        competenciaApi || "",
        grupoValue || "",
      );
      if (response.success) {
        setCompanies(response.data.companies || []);
        setCompetenciaResolved(response.data.competencia || "");
        setImportDataAvailable(response.data.importDataAvailable !== false);
      } else {
        setError(response.message || "Erro ao carregar a conferência de lançamentos.");
      }
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(competenciaInput, grupo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    load(competenciaInput, grupo);
  };

  // Campos derivados por empresa (diferença Hub x Portal, situação, score de
  // prioridade) — calculados uma vez para alimentar ordenação, busca e exportação.
  const enriched = useMemo(
    () =>
      companies.map((c) => {
        const hub = c.hub?.notasHub ?? 0;
        const portal = c.portal?.notasPortal;
        const diferenca = portal != null ? hub - portal : null;
        const diferencaAbs = diferenca != null ? Math.abs(diferenca) : -1;
        const portalIncompleto = portal != null && diferenca !== 0;
        const naoImportadas = c.importado?.naoImportadas ?? 0;
        // Prioriza portal incompleto (não dá pra confiar no "importado") e,
        // dentro disso, quem tem mais notas não importadas.
        const issueScore = (portalIncompleto ? 1_000_000 : 0) + Math.max(naoImportadas, 0);
        return {
          ...c,
          _diferenca: diferenca,
          _diferencaAbs: diferencaAbs,
          _portalIncompleto: portalIncompleto,
          _issueScore: issueScore,
        };
      }),
    [companies],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enriched;
    return enriched.filter(
      (c) =>
        String(c.code ?? "").toLowerCase().includes(q) ||
        String(c.name ?? "").toLowerCase().includes(q) ||
        String(c.cnpj ?? "").toLowerCase().includes(q),
    );
  }, [enriched, search]);

  const sorted = useMemo(() => {
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortConfig.key) {
        case "code":
          cmp = (Number(a.code) || 0) - (Number(b.code) || 0);
          break;
        case "name":
          cmp = String(a.name || "").localeCompare(String(b.name || ""));
          break;
        case "portal":
          cmp = (a.portalAccess?.hasAccess ? 1 : 0) - (b.portalAccess?.hasAccess ? 1 : 0);
          break;
        case "hubPortal":
          cmp = a._diferencaAbs - b._diferencaAbs;
          break;
        case "importado":
          cmp = a._issueScore - b._issueScore;
          break;
        default:
          cmp = 0;
      }
      if (cmp === 0) cmp = (Number(a.code) || 0) - (Number(b.code) || 0);
      return cmp * dir;
    });
    return arr;
  }, [filtered, sortConfig]);

  useEffect(() => {
    setPage(1);
  }, [search, sortConfig, grupo, competenciaResolved, pageSize]);

  const pageData = useMemo(
    () => paginateData(sorted, page, pageSize),
    [sorted, page, pageSize],
  );

  const sortState = useMemo(
    () => [
      {
        sortKey: sortConfig.key,
        direction: sortConfig.direction === "asc" ? "ascending" : "descending",
      },
    ],
    [sortConfig.key, sortConfig.direction],
  );

  const sortPlugin = useTableSortable({
    sort: sortState,
    onSortChange: (next) => {
      const entry = next[0];
      if (!entry) return;
      setSortConfig({
        key: entry.sortKey,
        direction: entry.direction === "ascending" ? "asc" : "desc",
      });
    },
    allowUnsortedState: false,
  });

  const stickyPlugin = useTableStickyColumns({ startKeys: ["code", "name"] });

  const paginationPlugin = useTablePagination({
    page,
    onPageChange: setPage,
    totalItems: sorted.length,
    pageSize,
    onPageSizeChange: setPageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    align: "end",
  });

  const handleExportExcel = () => {
    const rows = [
      [
        "Código",
        "Razão",
        "CNPJ",
        "Portal",
        "Notas Hub (processamento)",
        "Notas Portal",
        "Diferença",
        "Notas Hub (competência)",
        "Importadas",
        "Não Importadas",
        "Situação",
      ],
      ...sorted.map((r) => [
        r.code ?? "",
        r.name ?? "",
        r.cnpj ?? "",
        r.portalAccess?.hasAccess ? "Sim" : "Não",
        r.hub?.notasHub ?? 0,
        r.portal?.notasPortal != null ? r.portal.notasPortal : "—",
        r._diferenca != null ? r._diferenca : "—",
        r.importado?.totalHub ?? 0,
        r.importado?.importadas != null ? r.importado.importadas : "—",
        r.importado?.naoImportadas != null ? r.importado.naoImportadas : "—",
        r._portalIncompleto ? "Portal Incompleto" : "OK",
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Conferencia de Lancamentos");
    const comp = (competenciaResolved || "atual").replace(/-/g, "");
    XLSX.writeFile(wb, `Conferencia_Lancamentos_${comp}.xlsx`);
  };

  const columns = [
    {
      key: "code",
      header: "Código",
      width: pixel(80),
      sortable: true,
      renderCell: (r) => <Text size="sm">{r.code}</Text>,
    },
    {
      key: "name",
      header: "Razão",
      width: pixel(220),
      sortable: true,
      renderCell: (r) => <Text size="sm">{r.name}</Text>,
    },
    {
      key: "portal",
      header: "Portal",
      width: proportional(1),
      sortable: true,
      renderCell: (r) => (
        <Badge
          variant={r.portalAccess?.hasAccess ? "success" : "neutral"}
          label={r.portalAccess?.hasAccess ? "Sim" : "Não"}
        />
      ),
    },
    {
      key: "hubPortal",
      header: "Hub x Portal",
      width: proportional(2),
      sortable: true,
      renderCell: (r) => {
        const hub = r.hub?.notasHub ?? 0;
        const portal = r.portal?.notasPortal;
        if (portal == null) {
          return (
            <HStack gap={1} vAlign="center">
              <TwoColorBar
                greenValue={hub}
                orangeValue={0}
                total={hub}
                greenLabel={`${hub}`}
                orangeLabel=""
              />
              <Tooltip content="Sem contagem do portal neste período (empresa sem download compatível ou coleta só por certificado).">
                <FontAwesomeIcon icon="info-circle" style={{ opacity: 0.45, fontSize: 11 }} />
              </Tooltip>
            </HStack>
          );
        }
        const min = Math.min(hub, portal);
        const diff = Math.abs(hub - portal);
        const total = Math.max(hub, portal);
        return (
          <TwoColorBar
            greenValue={min}
            orangeValue={diff}
            total={total}
            greenLabel={`${min}`}
            orangeLabel={diff > 0 ? `${diff}` : ""}
          />
        );
      },
    },
    {
      key: "importado",
      header: "Importado",
      width: proportional(2),
      sortable: true,
      renderCell: (r) => {
        const totalCompetencia = r.importado?.totalHub ?? 0;
        if (r._portalIncompleto) {
          return <Badge variant="warning" label="Portal Incompleto" />;
        }
        if (!importDataAvailable || r.importado?.importadas == null) {
          return (
            <Text size="xs" type="supporting">
              —
            </Text>
          );
        }
        const importadas = r.importado.importadas;
        const naoImportadas = r.importado.naoImportadas;
        return (
          <TwoColorBar
            greenValue={importadas}
            orangeValue={naoImportadas}
            total={totalCompetencia}
            greenLabel={`${importadas}`}
            orangeLabel={naoImportadas > 0 ? `${naoImportadas}` : ""}
          />
        );
      },
    },
  ];

  if (loading) {
    return (
      <AstryxTheme>
        <Card padding={6}>
          <Stack gap={2}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height={48} index={i} />
            ))}
            <Text type="supporting" justify="center">
              Carregando conferência de lançamentos...
            </Text>
          </Stack>
        </Card>
      </AstryxTheme>
    );
  }

  return (
    <AstryxTheme>
      <Stack as="section" aria-label="Conferência de Lançamentos" gap={4} width="100%">
        <Stack gap={1}>
          <HStack gap={3} vAlign="center" hAlign="between" wrap="wrap">
            <Heading level={2}>Conferência de Lançamentos</Heading>
            <HStack gap={2} vAlign="center">
              <Button
                label="Exportar Excel"
                icon={<FontAwesomeIcon icon="file-excel" />}
                onClick={handleExportExcel}
                isDisabled={sorted.length === 0}
              />
              <IconButton
                label="Atualizar"
                tooltip="Atualizar"
                icon={<FontAwesomeIcon icon="sync" />}
                onClick={() => load(competenciaInput, grupo)}
              />
            </HStack>
          </HStack>
          {competenciaResolved && (
            <Text type="supporting">
              Competência {apiCompetenciaToMmAaaa(competenciaResolved)} — {sorted.length} de{" "}
              {companies.length} empresa{companies.length !== 1 ? "s" : ""}
              {search ? " (filtradas)" : ""}
            </Text>
          )}
        </Stack>

        <Card padding={4}>
          <Stack gap={3}>
            <Grid columns={{ minWidth: 200, max: 4 }} gap={3}>
              <TextInput
                label="Competência"
                placeholder="MM/AAAA"
                value={competenciaInput}
                onChange={setCompetenciaInput}
              />
              <Selector
                label="Grupo"
                options={GRUPO_OPTIONS}
                value={grupo}
                onChange={setGrupo}
              />
              <Stack justify="end">
                <Button
                  label="Filtrar"
                  variant="primary"
                  icon={<FontAwesomeIcon icon="search" />}
                  onClick={handleFilter}
                />
              </Stack>
              <TextInput
                label="Buscar empresa"
                placeholder="Código, razão ou CNPJ"
                value={search}
                onChange={setSearch}
              />
            </Grid>
          </Stack>
        </Card>

        {error && (
          <Card padding={6}>
            <Stack gap={3} hAlign="center">
              <EmptyState
                title="Erro ao carregar"
                description={error}
                icon={<FontAwesomeIcon icon="exclamation-triangle" />}
              />
              <Button
                label="Tentar Novamente"
                variant="primary"
                icon={<FontAwesomeIcon icon="sync" />}
                onClick={() => load(competenciaInput, grupo)}
              />
            </Stack>
          </Card>
        )}

        {!error && companies.length === 0 && (
          <Card padding={6}>
            <EmptyState
              title="Nenhuma empresa encontrada"
              description="Não há empresas ativas para os filtros selecionados."
              icon={<FontAwesomeIcon icon="building" />}
            />
          </Card>
        )}

        {!error && companies.length > 0 && sorted.length === 0 && (
          <Card padding={6}>
            <EmptyState
              title="Nenhum resultado para a busca"
              description="Ajuste o termo pesquisado ou limpe o campo de busca."
              icon={<FontAwesomeIcon icon="search" />}
            />
          </Card>
        )}

        {!error && sorted.length > 0 && (
          <Card padding={0}>
            <Table
              data={pageData.map((c) => ({ ...c, _rowId: c.companyId }))}
              columns={columns}
              idKey="_rowId"
              density="compact"
              hasHover
              plugins={{ sort: sortPlugin, sticky: stickyPlugin, pagination: paginationPlugin }}
            />
          </Card>
        )}
      </Stack>
    </AstryxTheme>
  );
}
